
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { EmailUpdateDialog } from "@/components/auth/EmailUpdateDialog";
import { SMSPreferencesSection } from "@/components/user/SMSPreferencesSection";
import PushNotificationsSection from "@/components/user/PushNotificationsSection";

const PersonalInfoForm = () => {
  const { profile, updateProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState({
    fullName: "",
    businessName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: ""
  });

  // Update local state when profile data changes
  useEffect(() => {
    if (profile) {
      setUserData({
        fullName: profile.full_name || "",
        businessName: profile.business_name || "",
        email: profile.email || "",
        phone: profile.phone_number || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        zipCode: profile.zip_code || ""
      });
    }
  }, [profile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await updateProfile({
        full_name: userData.fullName,
        business_name: userData.businessName || null,
        email: userData.email,
        phone_number: userData.phone,
        address: userData.address,
        city: userData.city || null,
        state: userData.state || null,
        zip_code: userData.zipCode || null
      });
      
      toast({
        title: "Profile updated",
        description: "Your profile information has been updated successfully.",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update failed",
        description: "There was a problem updating your profile.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setUserData({
        fullName: profile.full_name || "",
        businessName: profile.business_name || "",
        email: profile.email || "",
        phone: profile.phone_number || "",
        address: profile.address || "",
        city: profile.city || "",
        state: profile.state || "",
        zipCode: profile.zip_code || ""
      });
    }
    setIsEditing(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Personal Information</CardTitle>
            {!isEditing && (
              <Button 
                type="button"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-6">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={userData.fullName}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  name="businessName"
                  value={userData.businessName}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={userData.email}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => setShowEmailDialog(true)}
                  >
                    Change Email
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This is your login email address
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneInput
                  id="phone"
                  value={userData.phone}
                  onChange={(value) => {
                    const syntheticEvent = {
                      target: {
                        name: 'phone',
                        value: value
                      }
                    } as React.ChangeEvent<HTMLInputElement>;
                    handleChange(syntheticEvent);
                  }}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                  showValidation={true}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  name="address"
                  value={userData.address}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={userData.city}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  name="state"
                  value={userData.state}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                  maxLength={2}
                />
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="zipCode">ZIP Code</Label>
                <Input
                  id="zipCode"
                  name="zipCode"
                  value={userData.zipCode}
                  onChange={handleChange}
                  required
                  disabled={!isEditing}
                  className={!isEditing ? "bg-muted cursor-not-allowed" : "bg-muted"}
                />
              </div>
            </div>
            
            {isEditing && (
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
                  disabled={isLoading}
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <EmailUpdateDialog
          currentEmail={userData.email}
          userType="seller"
          open={showEmailDialog}
          onOpenChange={setShowEmailDialog}
          onSuccess={() => {
            setShowEmailDialog(false);
            toast({
              title: "Verification Email Sent",
              description: "Please check your new email address to complete the change.",
            });
          }}
        />
      </form>

      {/* SMS Preferences Section */}
      {profile && (
        <div className="mt-6">
          <SMSPreferencesSection
            userType="seller"
            initialSmsOptIn={profile.sms_opt_in || false}
            userId={profile.id}
          />
          <PushNotificationsSection isEditing={true} />
        </div>
      )}
    </>
  );
};

export default PersonalInfoForm;
